import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { supabase } from "../../config/supabaseClient";
import { Link, useLocation } from "react-router-dom";
import { IoMdPerson } from "react-icons/io";
import { CiCalendarDate } from "react-icons/ci";
import { FaAngleRight } from "react-icons/fa";
import moment from "moment/moment";

const PageWrapper = styled.div`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f8f9fa;
  min-height: 100vh;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const ArticleCard = styled(Link)`
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-5px);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const CardExcerpt = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1rem;
  // max-height: 4.5em;
  overflow: hidden;
  position: relative;

  /* Add ellipsis at the end */
  &:after {
    content: "...";
    position: absolute;
    bottom: 0;
    right: 0;
    padding-left: 1rem;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), white 50%);
  }

  /* Styling for rich text content */
  p {
    margin: 0.3em 0;
  }

  a,
  .article-link {
    color: rgb(167, 140, 76)!important;
    text-decoration: underline;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.5em 0 0.3em;
    font-size: 1em;
    font-weight: 600;
  }

  ul,
  ol {
    padding-left: 1.5em;
    margin: 0.3em 0;
  }
`;

const Header = styled.h1`
  font-size: 3rem;
  text-align: center;
  font-family: "Paris avenue";
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  background-color: rgb(167, 140, 76)!important;
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #bc9f8b;
  }
`;
const AuthorAndDate = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.875rem;
  color: #888;

  i {
    color: #071d17;
  }

  span {
    margin-left: 5px;
    color: #555;
  }
`;
const Button = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #000;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.3s ease;

  svg {
    font-size: 1.2rem;
  }
  &:hover {
    background-color: rgb(167, 140, 76);
  }
`;

const ArticleGrid = () => {
  const location = useLocation();
  const [articles, setArticles] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [page, setPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const articlesPerPage = 6;

  // Fetch total number of articles
  useEffect(() => {
    const fetchTotalArticles = async () => {
      let query = supabase
        .from("articlesdb")
        .select("*")
        .order("created_at", { ascending: false });
    
      if (location?.pathname === "/articles") {
        query = query.range((page - 1) * articlesPerPage, page * articlesPerPage - 1);
      } else {
        query = query.limit(3);
      }
    
      const { data, error } = await query;
    
      if (error) {
        console.error("Error fetching articles:", error);
      } else {
        setArticles(data);
      }
    };
    
    fetchTotalArticles();
  }, []);

  // Fetch articles for the current page
  useEffect(() => {
    const fetchArticles = async () => {
      const { data, error } = await supabase
        .from("articlesdb")
        .select("*")
        .order("created_at", { ascending: false })
        .range((page - 1) * articlesPerPage, page * articlesPerPage - 1);

      if (error) {
        console.error("Error fetching articles:", error);
      } else {
        setArticles(data);
      }
    };

    fetchArticles();
  }, [page]);

  // Fetch signed URLs for images
  useEffect(() => {
    const fetchSignedUrls = async () => {
      const urls = {};
      for (const article of articles) {
        const { data: signedUrlData, error } = await supabase.storage
          .from("articlestorage")
          .createSignedUrl(article.image_link, 60);

        if (error) {
          console.error("Error getting signed URL:", error);
        } else {
          urls[article.id] = signedUrlData.signedUrl;
        }
      }
      setSignedUrls(urls);
    };

    if (articles.length > 0) {
      fetchSignedUrls();
    }
  }, [articles]);

  const totalPages = Math.ceil(totalArticles / articlesPerPage);


  return (
    <PageWrapper>
      <Header style={{margin: `${location?.pathname === "/articles" ? `8rem 0 0rem 0` : `2rem 0 0rem 0`}`}}>Market Insights
      </Header>
      <p style={{marginTop:"0px"}}>Stay updated with our latest articles and news.</p>
      <GridContainer>
        {articles.map((article) => (
          <ArticleCard key={article.id} to={`/article/${article.id}`}>
            {signedUrls[article.id] && (
              <CardImage src={signedUrls[article.id]} alt={article.title} />
            )}
            <CardContent>
              <div className="title-desc-fix-height">
                <CardTitle>{article.title}</CardTitle>
                <CardExcerpt>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: article.content.split("</p>")[0] + "</p>",
                    }}
                  />
                </CardExcerpt>
              </div>
              <AuthorAndDate>
                <IoMdPerson /> <span> Embark Property </span> <CiCalendarDate />
                <span> {moment(article?.article).format("MMM DD, YYYY")} </span>
              </AuthorAndDate>
              <Button>
                Continue Reading <FaAngleRight />
              </Button>
            </CardContent>
          </ArticleCard>
        ))}
      </GridContainer>
   {location?.pathname === "/articles" && <Pagination>
        <PageButton onClick={() => setPage(page - 1)} disabled={page === 1}>
          Previous
        </PageButton>
        <span>
          Page {page} of {totalPages}
        </span>
        <PageButton
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
        >
          Next
        </PageButton>
      </Pagination>}
    </PageWrapper>
  );
};

export default ArticleGrid;
